<!-- A widget that shows a parent Store with child Decoders.

The child Decoders are those that use the current Ticker.

-->

<template>
  <div>
    <!-- Header -->
    <detail-entries-header
      v-if="showHeader"
      :prefix1="activeOnly ? $t('activeDecoders') : $t('futureDecoders')"
      :num1="getFilteredEntries(entries).length"
      show-expander
      :all-expanded.sync="allExpanded"
    />
    <!-- Stores -->
    <v-list
      v-for="(wrapper, wrapperIndex) in sortedWrappers"
      :key="wrapper.store.id"
      dense
    >
      <v-list-tile
        avatar
        class="store-tile"
        @click.stop="toggleWrapperExpanded(wrapper)"
      >
        <v-list-tile-avatar>
          <v-icon
            large
            class="msr--text"
          >
            location_on
          </v-icon>
        </v-list-tile-avatar>
        <v-list-tile-content>
          <v-list-tile-sub-title>
            <small>{{ wrapper.store.name }}</small>
          </v-list-tile-sub-title>
          <v-list-tile-title>
            <span class="grey--text text--darken-3">
              <span style="font-size: 1.25em">
                <strong>{{ wrapper.store.city }}</strong>
                {{ wrapper.store.location || '' }}
              </span>
            </span>
            <div v-if="wrapper.store.address">
              {{ wrapper.store.address }}
            </div>
          </v-list-tile-title>
        </v-list-tile-content>
        <v-list-tile-action>
          <v-icon
            medium
            class="grey--text"
            :class="{flipped: wrapper.expanded}"
          >
            keyboard_arrow_down
          </v-icon>
        </v-list-tile-action>
      </v-list-tile>
      <v-divider
        v-if="wrapper.expanded"
        class="inset-left-within-container mt-1"
      />
      <!-- Decoders that use the current Ticker -->
      <transition
        name="list-transition"
        mode="out-in"
      >
        <div
          v-if="wrapper.expanded"
          class="list"
        >
          <ticker-decoder-entry
            v-for="(entry, entryIndex) in wrapper.entries"
            :key="entry.id"
            class="inset-left-within-container"
            :entry="entry"
            :show-selection-checkbox="showSelectionCheckboxes"
            :show-divider="entryIndex !== wrapper.entries.length - 1"
            @deleted="emitEntryDeleted()"
            @changed="$emit('changed')"
          />
        </div>
      </transition>
      <v-divider
        v-if="!wrapper.expanded && wrapperIndex !== sortedWrappers.length - 1"
      />
    </v-list>
  </div>
</template>

<script>
import groupBy from 'lodash.groupby';
import orderBy from 'lodash.orderby';

import commonEntry from '@/mixins/commonEntry.js';

import DetailEntriesHeader from '@/components/DetailEntriesHeader';
import TickerDecoderEntry from '@/components/TickerDecoderEntry';

/**
 * An object bundling a Store's Entries that use the current Ticker.
 */
class StoreEntriesWrapper {
  constructor(store, entries, expanded) {
    this.store = store;
    this.entries = entries;
    this.expanded = expanded;
  }
}

export default {
  components: {
    DetailEntriesHeader,
    TickerDecoderEntry,
  },
  mixins: [commonEntry],
  props: {
    entries: {
      type: Array[Object],
      required: true,
    },
    showHeader: {
      type: Boolean,
      default: false,
    },
    showDivider: {
      type: Boolean,
      default: true,
    },
    showSelectionCheckboxes: {
      type: Boolean,
      default: false,
    },
    showExpansionControl: {
      type: Boolean,
      default: true,
    },
    /* Must set one of the two below! */
    activeOnly: {
      type: Boolean,
      default: false,
    },
    futureOnly: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      sortedWrappers: [],
    };
  },
  computed: {
    allExpanded: {
      get() {
        const expandedWrappers = this.sortedWrappers.filter(
          (wrapper) => wrapper.expanded
        );
        return expandedWrappers.length === this.sortedWrappers.length;
      },
      set(expanded) {
        const newExpanded = expanded || !this.allExpanded;
        this.sortedWrappers.map((wrapper) => (wrapper.expanded = newExpanded));
      },
    },
  },
  watch: {
    /**
     * This only exists to properly initialize the array.
     *
     * If done within mounted() it may not yet have resolved into a list.
     */
    entries: function (newVal, oldVal) {
      this.sortedWrappers = this.createSortedWrappers();
    },
  },
  mounted() {
    if (!this.activeOnly && !this.futureOnly) {
      throw 'Set one of "activeOnly" or "futureOnly" props!';
    }
    if (this.activeOnly && this.futureOnly) {
      throw 'Set either "activeOnly" or "futureOnly" prop, cannot set both!';
    }

    this.sortedWrappers = this.createSortedWrappers();
    if (this.sortedWrappers.length <= 3) {
      this.allExpanded = true;
    }
  },
  methods: {
    /**
     * Return either active or future entries.
     */
    getFilteredEntries(entries) {
      if (this.futureOnly) {
        entries = this.getFutureEntries(entries);
      }
      if (this.activeOnly) {
        entries = this.getActiveEntries(entries);
      }
      return entries;
    },
    /**
     * Analyze given entries and wrap them in helper objects.
     *
     * Each wrapper wraps a Store and all Decoders for it and
     * remembers the Store's expansion state.
     *
     * Note: Wrappers that have no entries due to sorting are skipped
     *   from the result!
     *
     * This is intentionally not a computed property so that the
     * wrapper objects are reactive and updating them reflects in the UI.
     *
     * Args:
     *   keepExpansionStates (Boolean): Optional, true by default.
     *     Since the entries here are grouped below their Store which
     *     can be expanded/collapsed, we try to 'remember' the expansion
     *     states here. Otherwise any change to the list would collapse
     *     all wrappers, which can be quite annoying for the user.
     */
    createSortedWrappers(keepExpansionStates = true) {
      let wrappers = [];
      let entriesGroupedByStore = groupBy(this.entries, 'decoder.store.id');

      let storeIds = Object.keys(entriesGroupedByStore);
      // Sort stores by their city name.
      storeIds = orderBy(storeIds, (storeId) => {
        let entries = entriesGroupedByStore[storeId];
        let firstEntry = entries[0];
        return firstEntry.decoder.store.city;
      });

      // Create wrapper objects.
      storeIds.forEach((storeId) => {
        let entries = entriesGroupedByStore[storeId];
        // Apply futureOnly/activeOnly filtering.
        let wrapperEntries = this.getFilteredEntries(entries);
        if (!wrapperEntries.length) {
          return;
        }
        let store = wrapperEntries[0].decoder.store;

        let expanded = false;
        if (keepExpansionStates) {
          const oldWrapper = this.sortedWrappers.find(
            (wrapper) => wrapper.store.id == storeId
          );
          if (oldWrapper) {
            expanded = oldWrapper.expanded;
          }
        }

        let wrapper = new StoreEntriesWrapper(store, wrapperEntries, expanded);
        wrappers.push(wrapper);
      });
      return wrappers;
    },
    toggleWrapperExpanded(wrapper) {
      if (!this.showExpansionControl) {
        return;
      }
      wrapper.expanded = !wrapper.expanded;
    },
    emitEntryDeleted() {
      this.$emit('entry-deleted');
    },
  },
};
</script>

<style scoped>
.list {
  transform-origin: top;
}

.list-transition-enter-active,
.list-transition-leave-active {
  transition: all 0.2s ease-in-out;
}
.list-transition-enter,
.list-transition-leave-to {
  opacity: 0;
  transform: scaleY(0);
}
</style>



// WEBPACK FOOTER //
// src/components/TickerDetailEntries.vue